import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

export function VolumeVisualizer({
  level,
  className,
}: {
  level?: number;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(80); // Default fallback (h-20 = 80px)

  useEffect(() => {
    if (containerRef.current) {
      const height = containerRef.current.offsetHeight;
      setMaxHeight(height);
    }
  }, []);

  const barHeights = generateBarHeights(level ?? null, maxHeight);

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex h-20 w-20 items-center justify-center gap-2",
        className,
      )}
    >
      {barHeights.map((height, index) => (
        <div
          key={index}
          className="transition-height w-1 rounded-full bg-blue-700 duration-50 ease-out"
          style={{ height: `${height}%` }}
        />
      ))}
    </div>
  );
}

function generateBarHeights(level: number | null, maxHeight: number) {
  if (level === null) {
    // Return minimum height as percentage of container
    const minHeightPercentage = (5 / maxHeight) * 100;
    return Array.from({ length: 7 }, () => minHeightPercentage);
  }

  const barCount = 7;
  const middleBar = Math.floor(barCount / 2);
  const waveIntensity = 1;
  const organicness = 1;

  return Array.from({ length: barCount }, (_, i) => {
    // Base height determined by distance from middle (creates the wave shape)
    const distanceFromMiddle = Math.abs(i - middleBar);
    const maxMultiplier = 1 - (distanceFromMiddle / (barCount / 2)) * 0.7; // Increased effect of distance

    // Wave effect that emanates from center
    // Use cosine to make the center the peak of the wave
    const wavePhase = Date.now() / 500;
    // Distance-based wave effect - farther bars get the wave later
    const waveDelay = distanceFromMiddle * 0.5 * waveIntensity;
    const waveOffset = Math.cos(wavePhase - waveDelay) * 15 * waveIntensity;

    // Add randomness for organic feel
    const randomFactor = (Math.random() - 0.5) * 10 * organicness;

    // Combine all factors - scale to container height
    let heightInPixels =
      level * maxHeight * maxMultiplier + waveOffset + randomFactor;

    // Make middle bar consistently larger
    if (i === middleBar) {
      heightInPixels = heightInPixels * 1.2; // Boost the middle bar
    }

    // Ensure minimum height and max height constraints
    const minHeightPixels = 5;
    heightInPixels = Math.min(
      Math.max(heightInPixels, minHeightPixels),
      maxHeight,
    );

    // Convert to percentage of container height
    const heightPercentage = (heightInPixels / maxHeight) * 100;
    return heightPercentage;
  });
}
